import React, { useLayoutEffect, useRef, useState } from 'react';
import { FLASHCARD_DEMO } from '../../data/onboardingFlashcardDemo';
import { easeInOut, easeOut, mix, span, useDemoTime, useStageScale } from './demoTimeline';
import './flashcardDemo.css';

// One real flashcard generation, replayed with the waiting cut out.
// Everything is derived from a single clock so the loop is deterministic and recordable.
const STAGE_WIDTH = 480;
const STAGE_HEIGHT = 560;
const LOOP_MS = 16500;
const STILL_MS = 12600;
const TICK_AT = [800, 1120, 1440, 1760];
const PRESS_AT = 2450;
const EVIDENCE = ['Course framework', 'Scoring guidelines', 'Scored student answers', 'Chief Reader reports'];

function Highlighted({ text, phrase, progress }) {
  const index = phrase ? text.indexOf(phrase) : -1;
  if (index < 0) return text;
  return (
    <>
      {text.slice(0, index)}
      <mark style={{ backgroundSize: `${progress * 100}% 72%` }}>{phrase}</mark>
      {text.slice(index + phrase.length)}
    </>
  );
}

// Glides between waypoints ({ at, x, y }), pressing at each tick and at the build button.
function Cursor({ t, path }) {
  if (!path?.length) return null;
  let x = path[0].x;
  let y = path[0].y;
  for (let i = 1; i < path.length; i += 1) {
    const move = easeInOut(span(t, path[i].at - 260, path[i].at));
    if (move <= 0) break;
    x = mix(path[i - 1].x, path[i].x, move);
    y = mix(path[i - 1].y, path[i].y, move);
  }
  const press = Math.max(0, ...[...TICK_AT, PRESS_AT].map((at) => 1 - Math.abs(t - at) / 120));
  const opacity = span(t, 250, 500) * (1 - span(t, 2700, 2950));
  return (
    <svg className="fcd-cursor" viewBox="0 0 24 24" aria-hidden="true" style={{ opacity, transform: `translate(${x}px, ${y}px) scale(${1 - press * 0.14})` }}>
      <path d="M5 3l14 8.2-6.4 1.3 3.7 7.1-2.6 1.3-3.6-7.1L5 18.6z" />
    </svg>
  );
}

const Check = () => <svg viewBox="0 0 16 16"><path d="M3.5 8.4l2.8 2.8 6.2-6.4" /></svg>;

export default function FlashcardDemo({ className = '', freezeAt = null }) {
  const frameRef = useRef(null);
  const stageRef = useRef(null);
  const boxRefs = useRef([]);
  const buttonRef = useRef(null);
  const [path, setPath] = useState(null);
  const scale = useStageScale(frameRef, STAGE_WIDTH);
  const t = useDemoTime({ frameRef, loopMs: LOOP_MS, stillMs: STILL_MS, freezeAt });
  const { course, unit, unitPoints, points, coverage, deck } = FLASHCARD_DEMO;
  const card = deck.showcase;

  useLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;
    const measure = () => {
      const nextScale = frame.clientWidth / STAGE_WIDTH;
      const stage = stageRef.current?.getBoundingClientRect();
      const button = buttonRef.current?.getBoundingClientRect();
      const boxes = boxRefs.current.map((box) => box?.getBoundingClientRect());
      if (!stage || !button || boxes.some((box) => !box) || !nextScale) return;
      const local = (rect, dx, dy) => ({ x: (rect.left - stage.left) / nextScale + dx, y: (rect.top - stage.top) / nextScale + dy });
      setPath([
        { at: 0, x: STAGE_WIDTH - 60, y: STAGE_HEIGHT - 40 },
        ...boxes.map((box, index) => ({ at: TICK_AT[index], ...local(box, 6, 5) })),
        { at: PRESS_AT, ...local(button, 110, 16) },
      ]);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  const selected = TICK_AT.filter((at) => t >= at).length;
  const pickIn = easeOut(span(t, 0, 450));
  const pickOut = easeInOut(span(t, 2700, 3150));
  const buildIn = easeOut(span(t, 2950, 3450));
  const buildOut = easeInOut(span(t, 6500, 6950));
  const cardIn = easeOut(span(t, 6750, 7400));
  const loopOut = easeInOut(span(t, 15700, 16400));
  const pressed = t >= PRESS_AT - 70 && t < PRESS_AT + 150;
  const progress = easeInOut(span(t, 3300, 6300));
  const flip = easeInOut(span(t, 8800, 9500));
  const mark = easeOut(span(t, 9800, 10900));
  const receipt = easeOut(span(t, 11000, 11600));

  return (
    <div ref={frameRef} className={`fcd-frame ${className}`.trim()} role="img" aria-label={`A real ${course} flashcard deck being built from ${deck.selectionCount} syllabus points: ${deck.cardCount} cards from ${deck.sourceCount} sources`}>
      <div ref={stageRef} className="fcd-stage" style={{ transform: `scale(${scale})`, opacity: 1 - loopOut }}>

        <section className="fcd-scene fcd-pick" style={{ opacity: pickIn * (1 - pickOut), transform: `translateY(${mix(10, 0, pickIn) - pickOut * 14}px)`, filter: `blur(${pickOut * 6}px)` }}>
          <div className="fcd-row-between">
            <p className="fcd-eyebrow">Official syllabus</p>
            <p className="fcd-counter"><strong>{selected}</strong> / 5 selected</p>
          </div>
          <h3 className="fcd-title">Choose specification points</h3>
          <div className="fcd-unit"><span>{unit}</span><small>{unitPoints} points</small></div>
          <div className="fcd-points">
            {points.map((point, index) => {
              const isChecked = t >= TICK_AT[index];
              return (
                <div key={point.code} className={`fcd-point ${isChecked ? 'is-checked' : ''}`}>
                  <i ref={(node) => { boxRefs.current[index] = node; }} aria-hidden="true">{isChecked ? <Check /> : null}</i>
                  <p><span><b>{point.code}:</b> {point.text}</span><small>{point.type}</small></p>
                </div>
              );
            })}
          </div>
          <div ref={buttonRef} className={`fcd-build-button ${pressed ? 'is-pressed' : ''}`}>Review &amp; build <span aria-hidden="true">→</span></div>
        </section>

        <section className="fcd-scene fcd-build" style={{ opacity: buildIn * (1 - buildOut), transform: `translateY(${mix(14, 0, buildIn) - buildOut * 12}px)`, filter: `blur(${buildOut * 6}px)` }}>
          <p className="fcd-eyebrow">Step 3 · Building your deck</p>
          <h3 className="fcd-title">Turning the syllabus into recall</h3>
          <p className="fcd-sub">{course.replace(/^AP /, '')} · {deck.selectionCount} selected points · variable deck size</p>
          <div className="fcd-bar"><span style={{ transform: `scaleX(${progress})` }} /></div>
          <p className="fcd-label">Every statement covered</p>
          <ul className="fcd-statements">
            {coverage.map((statement, index) => {
              const appear = easeOut(span(t, 3500 + index * 380, 3900 + index * 380));
              const done = t >= 3900 + index * 380 + 250;
              return (
                <li key={statement.code} style={{ opacity: appear, transform: `translateX(${mix(-8, 0, appear)}px)` }} className={done ? 'is-done' : ''}>
                  <i aria-hidden="true">{done ? <Check /> : null}</i>
                  <b>{statement.code}</b><span>{statement.text}</span>
                </li>
              );
            })}
          </ul>
          <p className="fcd-label">Course evidence</p>
          <div className="fcd-evidence">
            {EVIDENCE.map((label, index) => {
              const lit = span(t, 4200 + index * 420, 4600 + index * 420);
              return <span key={label} className={lit >= 1 ? 'is-lit' : ''} style={{ opacity: mix(0.35, 1, lit) }}>{label}</span>;
            })}
          </div>
        </section>

        <section className="fcd-scene fcd-deck" style={{ opacity: cardIn, transform: `translateY(${mix(18, 0, cardIn)}px)` }}>
          <div className="fcd-stack">
            <div className="fcd-ghost is-back" style={{ transform: `translate(${mix(0, 14, cardIn)}px, ${mix(0, 14, cardIn)}px) rotate(${mix(0, 3, cardIn)}deg)` }} />
            <div className="fcd-ghost" style={{ transform: `translate(${mix(0, 7, cardIn)}px, ${mix(0, 7, cardIn)}px) rotate(${mix(0, 1.5, cardIn)}deg)` }} />
            <div className="fcd-card-rotator" style={{ transform: `rotateY(${flip * 180}deg)` }}>
              <div className="fcd-face fcd-face--question">
                <span className="fcd-tape" aria-hidden="true" />
                <div className="fcd-card-head"><span className="fcd-badge">Q</span><span className="fcd-kicker">Question</span></div>
                <p className="fcd-question">{card.front}</p>
                <div className="fcd-card-foot"><span>Tap the card to flip</span><kbd>Space</kbd></div>
              </div>
              <div className="fcd-face fcd-face--answer">
                <span className="fcd-tape" aria-hidden="true" />
                <div className="fcd-card-head"><span className="fcd-badge">A</span><span className="fcd-kicker">Answer</span></div>
                <p className="fcd-recap">{card.front}</p>
                <p className="fcd-answer"><Highlighted text={card.back} phrase={card.highlight} progress={mark} /></p>
              </div>
            </div>
          </div>
          <div className="fcd-receipt" style={{ opacity: receipt, transform: `translateY(${mix(10, 0, receipt)}px)` }}>
            <strong>Your deck is ready</strong>
            <span><b>{deck.cardCount}</b> cards</span>
            <span><b>{deck.selectionCount}</b> points</span>
            <span><b>{deck.sourceCount}</b> sources</span>
          </div>
        </section>

        <Cursor t={t} path={path} />
      </div>
    </div>
  );
}
